import { useEffect, useId, useLayoutEffect, useRef, useState } from "react";

import { Button } from "./Button";
import { IconButton } from "./IconButton";
import { Selector, type SelectorOption } from "./Selector";
import { TextInput, type TextInputProps } from "./TextInput";

const MONTH_OPTIONS: readonly SelectorOption<string>[] = [
  { value: "01", label: "January" },
  { value: "02", label: "February" },
  { value: "03", label: "March" },
  { value: "04", label: "April" },
  { value: "05", label: "May" },
  { value: "06", label: "June" },
  { value: "07", label: "July" },
  { value: "08", label: "August" },
  { value: "09", label: "September" },
  { value: "10", label: "October" },
  { value: "11", label: "November" },
  { value: "12", label: "December" },
];
const WEEKDAYS = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"];
const DAY_LABEL = new Intl.DateTimeFormat("en-US", {
  dateStyle: "full",
  timeZone: "UTC",
});

function CalendarIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      aria-hidden="true"
    >
      <rect x="4" y="5" width="16" height="16" rx="3" />
      <path d="M16 3v4M8 3v4M4 11h16M8 15h2M14 15h2M8 18h2" />
    </svg>
  );
}

function monthOffset(month: string, offset: number): string {
  const date = new Date(`${month}-01T00:00:00Z`);
  date.setUTCMonth(date.getUTCMonth() + offset);
  return date.toISOString().slice(0, 7);
}

export interface DateInputProps extends Omit<
  TextInputProps,
  "type" | "value" | "defaultValue" | "inputRef" | "iconRight"
> {
  value: string;
}

export function DateInput({
  value,
  className,
  onFocus,
  onBlur,
  onValueChange,
  min = "0001-01-01",
  max = "9999-12-31",
  disabled,
  ...props
}: DateInputProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const calendarRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const initialValue = useRef(value);
  const [editing, setEditing] = useState(false);
  const [open, setOpen] = useState(false);
  const today = new Date().toISOString().slice(0, 10);
  const lower = String(min);
  const upper = String(max);
  const clampDate = (date: string) =>
    date < lower ? lower : date > upper ? upper : date;
  const [month, setMonth] = useState(() => clampDate(today).slice(0, 7));
  const [yearDraft, setYearDraft] = useState(month.slice(0, 4));
  const calendarId = useId();

  useLayoutEffect(() => {
    const input = inputRef.current;
    if (input && !editing && input.value !== value) {
      input.value = value;
    }
  }, [editing, value]);

  useEffect(() => {
    if (!open) return;
    const closeOutside = (event: PointerEvent) => {
      if (
        event.target instanceof Node &&
        !rootRef.current?.contains(event.target)
      )
        setOpen(false);
    };
    document.addEventListener("pointerdown", closeOutside);
    const selected = calendarRef.current?.querySelector<HTMLButtonElement>(
      'button[data-date][aria-pressed="true"]:not(:disabled)',
    );
    const first = calendarRef.current?.querySelector<HTMLButtonElement>(
      "button[data-date]:not(:disabled)",
    );
    (selected ?? first)?.focus();
    return () => document.removeEventListener("pointerdown", closeOutside);
  }, [open]);

  const showMonth = (next: string) => {
    const bounded =
      next < lower.slice(0, 7)
        ? lower.slice(0, 7)
        : next > upper.slice(0, 7)
          ? upper.slice(0, 7)
          : next;
    setMonth(bounded);
    setYearDraft(bounded.slice(0, 4));
  };
  const closeCalendar = () => {
    setOpen(false);
    rootRef.current
      ?.querySelector<HTMLButtonElement>("[aria-controls]")
      ?.focus();
  };
  const selectDate = (date: string) => {
    const input = inputRef.current;
    if (!input) return;
    input.value = date;
    onValueChange?.(date);
    setOpen(false);
    input.focus();
  };
  const firstDay = new Date(`${month}-01T00:00:00Z`);
  const lastDay = new Date(firstDay);
  lastDay.setUTCMonth(lastDay.getUTCMonth() + 1, 0);
  const padding = (firstDay.getUTCDay() + 6) % 7;

  return (
    <div
      className="ui-date-input-root"
      ref={rootRef}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) setOpen(false);
      }}
      onKeyDown={(event) => {
        if (event.key === "Escape" && open) {
          event.preventDefault();
          event.stopPropagation();
          closeCalendar();
        }
      }}
    >
      <TextInput
        {...props}
        className={["ui-date-input", className].filter(Boolean).join(" ")}
        inputRef={inputRef}
        type="date"
        min={min}
        max={max}
        disabled={disabled}
        onValueChange={onValueChange}
        defaultValue={initialValue.current}
        iconRight={
          <IconButton
            label="Open calendar"
            tooltip="Choose a date"
            tone="quiet"
            size="sm"
            disabled={disabled}
            aria-expanded={open}
            aria-controls={calendarId}
            aria-haspopup="dialog"
            icon={<CalendarIcon />}
            onClick={() => {
              showMonth(
                clampDate(inputRef.current?.value || today).slice(0, 7),
              );
              setOpen(!open);
            }}
          />
        }
        onFocus={(event) => {
          setEditing(true);
          onFocus?.(event);
        }}
        onBlur={(event) => {
          setEditing(false);
          onBlur?.(event);
        }}
      />
      {open && !disabled && (
        <div
          id={calendarId}
          className="ui-date-calendar"
          role="dialog"
          aria-label="Choose date"
          ref={calendarRef}
        >
          <div className="ui-date-calendar__header">
            <IconButton
              label="Previous month"
              tooltip="Previous month"
              tone="quiet"
              size="xs"
              icon={<span aria-hidden="true">‹</span>}
              disabled={month <= lower.slice(0, 7)}
              onClick={() => showMonth(monthOffset(month, -1))}
            />
            <Selector
              ariaLabel="Calendar month"
              options={MONTH_OPTIONS}
              value={month.slice(5)}
              onValueChange={(next) =>
                showMonth(`${month.slice(0, 4)}-${next}`)
              }
            />
            <TextInput
              aria-label="Calendar year"
              className="ui-date-calendar__year"
              size="sm"
              inputMode="numeric"
              maxLength={4}
              value={yearDraft}
              onValueChange={(next) => {
                setYearDraft(next);
                if (
                  /^\d{4}$/.test(next) &&
                  next >= lower.slice(0, 4) &&
                  next <= upper.slice(0, 4)
                )
                  showMonth(`${next}-${month.slice(5)}`);
              }}
              onBlur={() => setYearDraft(month.slice(0, 4))}
            />
            <IconButton
              label="Next month"
              tooltip="Next month"
              tone="quiet"
              size="xs"
              icon={<span aria-hidden="true">›</span>}
              disabled={month >= upper.slice(0, 7)}
              onClick={() => showMonth(monthOffset(month, 1))}
            />
          </div>
          <div className="ui-date-calendar__grid">
            {WEEKDAYS.map((day) => (
              <span className="ui-date-calendar__weekday" key={day}>
                {day}
              </span>
            ))}
            {Array.from({ length: padding }, (_, index) => (
              <span key={`padding-${index}`} />
            ))}
            {Array.from({ length: lastDay.getUTCDate() }, (_, index) => {
              const day = index + 1;
              const date = `${month}-${String(day).padStart(2, "0")}`;
              return (
                <Button
                  key={date}
                  tone="quiet"
                  size="xs"
                  className="ui-date-calendar__day"
                  data-date={date}
                  aria-label={DAY_LABEL.format(new Date(`${date}T00:00:00Z`))}
                  aria-pressed={date === value}
                  aria-current={date === today ? "date" : undefined}
                  disabled={date < lower || date > upper}
                  onClick={() => selectDate(date)}
                  onKeyDown={(event) => {
                    const offset = {
                      ArrowLeft: -1,
                      ArrowRight: 1,
                      ArrowUp: -7,
                      ArrowDown: 7,
                    }[event.key];
                    if (offset === undefined) return;
                    event.preventDefault();
                    const target = `${month}-${String(day + offset).padStart(2, "0")}`;
                    calendarRef.current
                      ?.querySelector<HTMLButtonElement>(
                        `button[data-date="${target}"]:not(:disabled)`,
                      )
                      ?.focus();
                  }}
                >
                  {day}
                </Button>
              );
            })}
          </div>
          <div className="ui-date-calendar__footer">
            <span>Type a year to jump</span>
            <Button
              tone="quiet"
              size="xs"
              disabled={today < lower || today > upper}
              onClick={() => selectDate(today)}
            >
              Today
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
